import type { CommandBatch } from '../../editor/commandHistory.js';
import type { EditorCommands } from '../../editor/commands.js';
import type { AppState } from '../../state/appState.js';
import type { EditorNavigationController } from '../editorNavigation.js';
import type { EditorSidebarController } from '../editorSidebar.js';
import type { NodeEditorController } from '../nodeEditor.js';
import { viewportTargetFromEditorContext, viewportTargetFromSceneNode } from '../../state/viewportTargets.js';
import { createSceneTreeRenderer, type SceneTreeViewportHit } from './tree.js';
import { createSceneTreeSelectionController } from './selection.js';
import type { SceneFavoriteTarget, ViewportTarget } from '../../state/appState.js';
import type { SceneNodeId } from '../../scene/sceneState.js';

interface SceneTreeSetupDeps {
  appState: AppState;
  commandBatch: CommandBatch;
  commands: EditorCommands;
  editorNavigation: EditorNavigationController;
  editorSidebar: EditorSidebarController;
  nodeEditor: Pick<NodeEditorController, 'renderGraph'>;
  scheduleRender: () => void;
  loadComponentSlot(componentId: string | null, slot: 'geometry' | 'material', layerId?: string): boolean;
  syncEditor(): boolean;
  syncViewportToolbar?(): void;
  onFavoritesChanged?(): void;
  updateParams(): void;
  renderExternalInspector?(host: HTMLElement): boolean;
}

export interface SceneTreeController {
  render(): void;
  getViewportFocusTarget(): ViewportTarget | null;
  getSelectedSceneNodeId(): SceneNodeId | null;
  /** Handle a preview single click (select / drill into component / step out). */
  handleViewportClick(hit: SceneTreeViewportHit): void;
  selectSceneFavorite(target: SceneFavoriteTarget): void;
}

export function createSceneTreeController(deps: SceneTreeSetupDeps): SceneTreeController {
  const selection = createSceneTreeSelectionController(() => {
    // Scene selection owns the Inspector again; library targets are explicit
    // and never remain as a sticky overlay on scene properties.
    deps.appState.inspectorTarget = { type: 'scene' };
    deps.syncViewportToolbar?.();
    // Re-render so the boundary overlay reflects the new selection.
    // Focus mode also needs it to re-pick the rendered target.
    deps.scheduleRender();
  }, () => deps.appState.activeComponentId);
  const renderer = createSceneTreeRenderer({
    appState: deps.appState,
    commandBatch: deps.commandBatch,
    commands: deps.commands,
    editorNavigation: deps.editorNavigation,
    editorSidebar: deps.editorSidebar,
    selection,
    nodeEditor: deps.nodeEditor,
    onFavoritesChanged: deps.onFavoritesChanged,
    syncViewportToolbar: deps.syncViewportToolbar,
    renderEditorNavigation: deps.editorNavigation.render,
    scheduleRender: deps.scheduleRender,
    loadComponentSlot: deps.loadComponentSlot,
    syncEditor: deps.syncEditor,
    updateParams: deps.updateParams,
    renderExternalInspector: deps.renderExternalInspector,
  });

  return {
    render: renderer.render,
    getSelectedSceneNodeId(): SceneNodeId | null {
      return selection.getSelectedSceneNodeId();
    },
    handleViewportClick(hit: SceneTreeViewportHit): void {
      renderer.handleViewportClick(hit);
    },
    selectSceneFavorite(target: SceneFavoriteTarget): void {
      renderer.selectSceneFavorite(target);
    },
    getViewportFocusTarget(): ViewportTarget | null {
      const editorTarget = viewportTargetFromEditorContext(deps.appState.editorContext);
      if (
        editorTarget?.type === 'geometry-library'
        || editorTarget?.type === 'material-library'
      ) {
        return editorTarget;
      }

      const virtualTarget = selection.getSelectedVirtualTarget();
      if (virtualTarget === 'scene') return { type: 'scene' };
      if (virtualTarget === 'component' && deps.appState.activeComponentId) {
        return { type: 'component', componentId: deps.appState.activeComponentId };
      }
      return viewportTargetFromSceneNode(
        deps.appState,
        selection.getSelectedSceneNodeComponentId(),
        selection.getSelectedSceneNodeId(),
      );
    },
  };
}
